/**
 * =====================================================
 * AVA CLIENT - Home V8
 * =====================================================
 * Reestructurado: Modelo usuario-empresa.
 * - PJ: gestión de empresas + selector al crear solicitud
 * - PH: flujo directo (crea client automáticamente)
 * - Sección de empresas visible para PJ
 * =====================================================
 */
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  Loader2,
  ArrowRight,
  LogIn,
  CheckCircle,
  CheckCircle2,
  FileText,
  Clock,
  Plus,
  Eye,
  FileSearch,
  Building2,
  AlertTriangle,
  UserCheck,
  Camera,
  PenLine,
  ShieldCheck,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { getUserErrorMessage } from "@/lib/userErrorMessage";
import { useAuth } from "@/_core/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, MonoText, LogoMark, EmptyState, type StatusTone, requestStatusToTone } from "@ava/shared/components/design-system";
import { AppHeader } from "@/components/AppHeader";
import { CompanyManager } from "@/components/CompanyManager";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Sprint Lifecycle v2: `DOCUMENTS_PENDING` fue removido del enum del backend.
// El request permanece en `PROCESSING` durante toda la fase de revisión por
// compliance, incluso si hay documentos rechazados pendientes de reemplazo.
const ACTIVE_STATUSES = ["DRAFT", "SUBMITTED", "PROCESSING"];
// Estados finales (solo lectura)
const FINAL_STATUSES = ["COMPLETED", "REJECTED", "AUTO_REJECTED", "EXPIRED"];

// Checklist documental de la landing pública (informativo, no vinculante).
// Fuente: policy_document_requirements.json (nivel ESTANDAR) + labels del
// documentTypeRegistry. La lista vinculante se calcula en el trámite vía
// getDocumentRequirements según tipo de cliente y nivel de diligencia.
const LANDING_DOC_CARDS = [
  {
    key: "PH",
    icon: UserCheck,
    title: "Persona humana",
    subtitle: "Alta de cuenta personal",
    docs: [
      {
        title: "DNI",
        desc: "Es lo único que necesitás para empezar: capturás frente, dorso y una selfie en la verificación de identidad.",
        badge: "camera" as const,
        badgeLabel: "Lo verificamos con tu cámara",
      },
      {
        title: "Declaración jurada de ganancias",
        desc: "Solo si tu volumen de operaciones lo requiere; te la pedimos durante la revisión.",
        badge: "profile" as const,
        badgeLabel: "Según tu operatoria",
      },
      {
        title: "Recibo de sueldo u origen de ingresos",
        desc: "Recibo de sueldo o constancia que respalde tus ingresos, si tu operatoria lo requiere.",
        badge: "profile" as const,
        badgeLabel: "Según tu operatoria",
      },
    ],
  },
  {
    key: "PJ",
    icon: Building2,
    title: "Persona jurídica",
    subtitle: "Alta de empresa (S.A., S.R.L. y otras)",
    docs: [
      { title: "Constancia de CUIT", desc: "Emitida por AFIP." },
      { title: "Estatuto social o contrato constitutivo", desc: "Contrato social o acta constitutiva vigente." },
      {
        title: "DNI del representante legal",
        desc: "Frente y dorso. El representante valida su identidad con una captura en vivo.",
        badge: "camera" as const,
        badgeLabel: "Identidad verificada",
      },
      {
        title: "Acta de designación de autoridades",
        desc: "No hace falta si el estatuto contiene las autoridades vigentes.",
      },
      {
        title: "Formulario de estructura societaria",
        desc: "Beneficiarios finales de la sociedad.",
        badge: "profile" as const,
        badgeLabel: "Según tu perfil",
      },
    ],
  },
];

/**
 * Determina a qué página redirigir según el estado del trámite
 *
 * Sprint Lifecycle v2: el cliente puede entrar a `/documents` cuando
 * `status === "PROCESSING"` y hay docs `REJECTED` para reemplazar o
 * additional_document_requests `PENDING` para subir. El resto del slot
 * set queda read-only — la lógica fina vive en `Documents.tsx`.
 */
function getRedirectPath(request: any): { path: string; readOnly: boolean; stepName: string } {
  const status = request.status;

  if (FINAL_STATUSES.includes(status)) {
    return { path: `/onboarding/${request.requestId}/analyze`, readOnly: true, stepName: "Estado de solicitud" };
  }
  if (status === "SUBMITTED") {
    if (request.hasPendingAdditionalDocs) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documentos adicionales pendientes" };
    }
    if (!request.allInitialDocsComplete) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documento requerido pendiente de carga" };
    }
    return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Ver estado de solicitud" };
  }
  if (status === "PROCESSING") {
    // Si hay docs rechazados, llevamos al cliente a `/documents` para
    // que pueda subir reemplazos. Mantenemos el detalle en `analyze` para
    // visibilidad del estado general.
    if (request.hasDocsNeedingAction) {
      return { path: `/onboarding/${request.requestId}/documents`, readOnly: false, stepName: "Reemplazar documentos rechazados" };
    }
    if (request.hasPendingAdditionalDocs) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documentos adicionales pendientes" };
    }
    if (!request.allInitialDocsComplete) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documento requerido pendiente de carga" };
    }
    return { path: `/onboarding/${request.requestId}/analyze`, readOnly: true, stepName: "Estado de solicitud" };
  }
  if (status === "DRAFT") {
    return { path: `/onboarding/${request.requestId}/documents`, readOnly: false, stepName: "Carga de documentos" };
  }
  return { path: `/onboarding/${request.requestId}/documents`, readOnly: false, stepName: "Continuar trámite" };
}

// Mapea status del backend a tone del Design System.
// Mantenemos `barColor` con clases Tailwind porque el progress bar (l.481)
// usa un fondo sólido custom, no aplica el patrón bg-50/text-700 del badge.
function getStatusConfig(status: string): { label: string; tone: StatusTone; barColor: string } {
  switch (status) {
    case "DRAFT":
      return { label: "Borrador", tone: "neutral", barColor: "bg-gray-400" };
    case "SUBMITTED":
      return { label: "Enviado", tone: "info", barColor: "bg-blue-400" };
    case "PROCESSING":
      return { label: "En proceso", tone: "info", barColor: "bg-blue-500" };
    case "COMPLETED":
      return { label: "Finalizado", tone: "success", barColor: "bg-green-500" };
    case "REJECTED":
      return { label: "Finalizado", tone: "danger", barColor: "bg-red-500" };
    case "AUTO_REJECTED":
      return { label: "Finalizado", tone: "danger", barColor: "bg-red-500" };
    case "EXPIRED":
      return { label: "Finalizado", tone: "neutral", barColor: "bg-gray-400" };
    default:
      return { label: status, tone: requestStatusToTone(status), barColor: "bg-gray-400" };
  }
}

type VerificationStatus = "sin_verificar" | "sin_cliente" | "pendiente" | "procesando" | "aprobada" | "rechazada" | "revision_manual" | null;
interface VerificationData {
  status: VerificationStatus;
  documentId?: number;
  hasDocument: boolean;
}

export default function Home() {
  const [, setLocation] = useLocation();
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const [showNewRequestDialog, setShowNewRequestDialog] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [openCompanyCreate, setOpenCompanyCreate] = useState(false);

  // Verificar estado de verificación de identidad
  useEffect(() => {
    async function checkVerification() {
      if (!user) return;
      setVerificationLoading(true);
      try {
        const response = await fetch("/api/verificacion/mi-estado");
        if (response.ok) {
          const data = await response.json();
          setVerificationData({ status: data.status, documentId: data.documentId, hasDocument: data.documentId != null });
        }
      } catch (err: unknown) {
        // Error silenciado
      } finally {
        setVerificationLoading(false);
      }
    }
    checkVerification();
  }, [user]);

  const verificationStatus = verificationData?.status ?? null;

  const { data: userRequests, isLoading: requestsLoading } = trpc.onboarding.list.useQuery(undefined, {
    enabled: !!user,
    retry: 1,
  });

  // Obtener el tipo de persona del cliente registrado
  const { data: myClient, isLoading: clientLoading } = trpc.auth.myClient.useQuery(undefined, {
    enabled: !!user,
    retry: 2,
  });
  const userClientType = myClient?.clientType ?? null;
  const isPJ = userClientType === "PERSONA_JURIDICA";

  // PH: Check identity verification status
  const identityQuery = trpc.onboarding.getIdentityStatus.useQuery(
    {},
    { enabled: !!user && !isPJ }
  );
  const phNeedsVerification = !isPJ && !identityQuery.data?.isValid;

  // Para PJ: obtener empresas
  const { data: companies } = trpc.auth.myCompanies.useQuery(undefined, {
    enabled: !!user && isPJ,
  });

  const createMutation = trpc.onboarding.create.useMutation({
    onSuccess: (data) => {
      toast.success("Solicitud creada exitosamente");
      setShowNewRequestDialog(false);
      setSelectedCompanyId(null);
      setLocation(`/onboarding/${data.requestId}/documents`);
    },
    onError: (error) => {
      toast.error("Error al crear solicitud: " + getUserErrorMessage(error));
    },
  });

  const handleCreateRequest = () => {
    if (isPJ) {
      // PJ: abrir dialog para seleccionar empresa
      setShowNewRequestDialog(true);
    } else {
      // PH: crear solicitud con client existente (creado por AVA-Identity callback)
      if (!myClient?.id) {
        toast.error("Debe verificar su identidad antes de crear una solicitud.");
        return;
      }
      createMutation.mutate({ clientId: myClient.id });
    }
  };

  const handleCreateRequestWithCompany = () => {
    if (!selectedCompanyId) {
      toast.error("Seleccioná una empresa para continuar");
      return;
    }
    createMutation.mutate({ clientId: selectedCompanyId });
  };

  const handleViewRequest = (request: any) => {
    const redirect = getRedirectPath(request);
    setLocation(redirect.path);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  // Separate active and completed requests
  const activeRequests = userRequests?.filter((r: any) => ACTIVE_STATUSES.includes(r.status)) || [];
  const completedRequests = userRequests?.filter((r: any) => FINAL_STATUSES.includes(r.status)) || [];
  const activeRequest = activeRequests[0]; // Primary active request

  // ============================================
  // NOT AUTHENTICATED - Landing page
  // ============================================
  if (!user) {
    // ============================================
    // PUBLIC LANDING (no autenticado) — AVA DS v4
    // Top bar sticky · Hero gradient con chip + tagline ·
    // Editorial section (tagline + stats + quote card) ·
    // Compliance badges (UIF, BCRA, CNV, Ley 25.326) · Trust strip
    // ============================================
    return (
      <div className="min-h-screen flex flex-col bg-white">
        {/* Top bar sticky */}
        <header
          className="sticky top-0 z-50 border-b"
          style={{
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(8px)",
            borderColor: "var(--ava-border, var(--border))",
          }}
        >
          <div
            className="mx-auto flex items-center justify-between gap-3"
            style={{ maxWidth: 1200, padding: "12px clamp(12px, 3vw, 24px)" }}
          >
            <div className="flex items-center gap-2.5">
              <LogoMark size={28} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.1 }}>AVA</div>
                <div style={{ fontSize: 11, color: "var(--ava-fg-muted, var(--muted-foreground))", marginTop: 1 }}>
                  Onboarding
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setLocation("/login")}>
                Iniciar sesión
              </Button>
              <Button onClick={() => setLocation("/register")}>Crear cuenta</Button>
            </div>
          </div>
        </header>

        {/* Hero gradient con chip y tagline */}
        <section
          className="relative overflow-hidden text-white flex-shrink-0"
          style={{ background: "var(--ava-gradient-hero, linear-gradient(135deg, #0B3D91 0%, #1565C0 30%, #1E88E5 60%, #3B8FF5 100%))" }}
        >
          <svg
            className="absolute left-0 right-0 bottom-0 w-full"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            style={{ height: "55%" }}
          >
            <path
              fill="rgba(255,255,255,0.15)"
              d="M0,160 C200,200 400,140 720,170 S1240,220 1440,140 L1440,320 L0,320 Z"
            />
          </svg>
          <svg
            className="absolute left-0 right-0 bottom-0 w-full"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            style={{ height: "40%" }}
          >
            <path
              fill="rgba(255,255,255,0.08)"
              d="M0,220 C250,180 480,260 720,220 S1200,170 1440,200 L1440,320 L0,320 Z"
            />
          </svg>
          <div
            className="relative mx-auto"
            style={{ maxWidth: 1200, padding: "clamp(28px, 6vw, 64px) clamp(16px, 4vw, 32px)" }}
          >
            <div
              className="inline-flex items-center gap-2 mb-4"
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                backdropFilter: "blur(6px)",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#7CFFB2",
                  boxShadow: "0 0 0 3px rgba(124,255,178,0.2)",
                }}
              />
              Portal del solicitante
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(26px, 5vw, 42px)",
                fontWeight: 700,
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
                maxWidth: 760,
              }}
            >
              Onboarding digital seguro y eficiente.
            </h1>
            <p
              style={{
                margin: "12px 0 0",
                fontSize: "clamp(14px, 2vw, 17px)",
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.5,
                maxWidth: 580,
              }}
            >
              Completá tu alta como cliente en minutos. Verificación de identidad biométrica y carga de
              documentos guiada paso a paso.
            </p>
            <div className="flex gap-3 mt-6 flex-wrap">
              <Button
                size="lg"
                onClick={() => setLocation("/login")}
                style={{
                  background: "#fff",
                  color: "var(--ava-primary, #0B3D91)",
                  borderColor: "transparent",
                  fontWeight: 600,
                  height: 44,
                }}
              >
                <LogIn className="w-4 h-4" />
                Iniciar sesión
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => setLocation("/register")}
                style={{
                  background: "transparent",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  fontWeight: 600,
                  height: 44,
                }}
              >
                Crear cuenta
              </Button>
            </div>
          </div>
        </section>

        {/* Editorial value-prop: 2 cols (left tagline+stats / right quote) */}
        <section
          className="mx-auto w-full"
          style={{ maxWidth: 1200, padding: "clamp(40px, 8vw, 96px) clamp(16px, 4vw, 32px)" }}
        >
          <div
            className="grid items-center"
            style={{
              gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
              gap: "clamp(24px, 6vw, 80px)",
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--ava-primary, #0B3D91)",
                }}
              >
                Onboarding regulado
              </p>
              <h2
                style={{
                  margin: "12px 0 0",
                  fontSize: "clamp(26px, 5vw, 44px)",
                  fontWeight: 700,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.08,
                }}
              >
                Hacé tu alta como cliente en una sola sesión.
              </h2>
              <p
                style={{
                  margin: "16px 0 0",
                  fontSize: "clamp(14px, 2vw, 17px)",
                  color: "var(--ava-fg-muted, var(--muted-foreground))",
                  lineHeight: 1.6,
                  maxWidth: 560,
                }}
              >
                AVA es la plataforma de compliance que usan bancos, fintech y empresas argentinas. Cumple con
                UIF, BCRA y CNV — y respeta tu privacidad de punta a punta.
              </p>

              <div
                className="grid mt-9 pt-7"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 24,
                  borderTop: "1px solid var(--ava-border, var(--border))",
                }}
              >
                {[
                  { v: "~8 min", l: "Duración promedio del alta completa." },
                  { v: "100%", l: "Datos cifrados, almacenamiento auditado." },
                  { v: "24/7", l: "Iniciá, pausá y retomá cuando quieras." },
                ].map((stat) => (
                  <div key={stat.v}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "clamp(28px, 5vw, 40px)",
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        fontFamily: "var(--ava-font-mono, 'JetBrains Mono', monospace)",
                      }}
                    >
                      {stat.v}
                    </p>
                    <p
                      style={{
                        margin: "6px 0 0",
                        fontSize: 12,
                        color: "var(--ava-fg-muted, var(--muted-foreground))",
                        lineHeight: 1.45,
                      }}
                    >
                      {stat.l}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <aside
              className="relative"
              style={{
                padding: "clamp(24px, 4vw, 36px)",
                background: "var(--ava-muted, #f8fafc)",
                borderRadius: 16,
                border: "1px solid var(--ava-border, var(--border))",
              }}
            >
              <p
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: 4,
                  left: 16,
                  fontSize: 96,
                  lineHeight: 1,
                  color: "var(--ava-primary, #0B3D91)",
                  opacity: 0.18,
                  fontFamily: "Georgia, serif",
                  margin: 0,
                }}
              >
                "
              </p>
              <p
                className="relative"
                style={{
                  margin: 0,
                  fontSize: "clamp(16px, 2vw, 19px)",
                  fontWeight: 500,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                }}
              >
                Empezás tu trámite, lo dejás cuando necesitás y lo retomás desde donde lo dejaste — sin volver
                a empezar.
              </p>
              <div
                className="flex items-center gap-3 mt-6 pt-4"
                style={{ borderTop: "1px solid var(--ava-border, var(--border))" }}
              >
                <LogoMark size={24} />
                <div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>AVA Onboarding</p>
                  <p style={{ margin: "1px 0 0", fontSize: 11, color: "var(--ava-fg-muted, var(--muted-foreground))" }}>
                    Plataforma de identidad digital
                  </p>
                </div>
              </div>
            </aside>
          </div>

          {/* Qué vas a necesitar — checklist documental PH/PJ (informativo) */}
          <div style={{ marginTop: "clamp(48px, 8vw, 88px)" }}>
            <p
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--ava-primary, #0B3D91)",
              }}
            >
              Antes de empezar
            </p>
            <h2
              style={{
                margin: "12px 0 0",
                fontSize: "clamp(26px, 5vw, 40px)",
                fontWeight: 700,
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
                maxWidth: 640,
              }}
            >
              Qué vas a necesitar para tu trámite.
            </h2>
            <p
              style={{
                margin: "16px 0 0",
                fontSize: "clamp(14px, 2vw, 17px)",
                color: "var(--ava-fg-muted, var(--muted-foreground))",
                lineHeight: 1.6,
                maxWidth: 600,
              }}
            >
              Tené estos documentos a mano. Los subís como foto o PDF, guiado paso a paso — y podés
              pausar y retomar cuando quieras.
            </p>

            <div
              className="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24, marginTop: 40 }}
            >
              {LANDING_DOC_CARDS.map((card) => (
                <div
                  key={card.key}
                  style={{
                    background: "var(--ava-card, #fff)",
                    border: "1px solid var(--ava-border, var(--border))",
                    borderRadius: 8,
                    boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                    padding: 24,
                  }}
                >
                  <div
                    className="flex items-center gap-3"
                    style={{ paddingBottom: 16, borderBottom: "1px solid var(--ava-border, var(--border))" }}
                  >
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: "var(--ava-info-50, #eff6ff)",
                        border: "1px solid var(--ava-info-200, #bfdbfe)",
                        color: "var(--ava-primary, #0B3D91)",
                      }}
                    >
                      <card.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{card.title}</h3>
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: 12,
                          color: "var(--ava-fg-muted, var(--muted-foreground))",
                        }}
                      >
                        {card.subtitle}
                      </p>
                    </div>
                  </div>
                  <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
                    {card.docs.map((doc, i) => (
                      <li
                        key={doc.title}
                        className="flex gap-3"
                        style={{
                          padding: "14px 0",
                          borderBottom:
                            i < card.docs.length - 1 ? "1px solid var(--ava-muted, #f8fafc)" : "none",
                        }}
                      >
                        <CheckCircle2
                          className="w-[18px] h-[18px] flex-shrink-0"
                          style={{ color: "var(--ava-success-700, #15803d)", marginTop: 1 }}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className="flex items-center gap-2 flex-wrap"
                            style={{ margin: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}
                          >
                            {doc.title}
                            {doc.badge && (
                              <span
                                className="inline-flex items-center gap-1"
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  letterSpacing: "0.02em",
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  whiteSpace: "nowrap",
                                  background:
                                    doc.badge === "camera"
                                      ? "var(--ava-info-50, #eff6ff)"
                                      : "var(--ava-neutral-50, #f8fafc)",
                                  color:
                                    doc.badge === "camera"
                                      ? "var(--ava-info-700, #1d4ed8)"
                                      : "var(--ava-neutral-700, #334155)",
                                  border:
                                    doc.badge === "camera"
                                      ? "1px solid var(--ava-info-200, #bfdbfe)"
                                      : "1px solid var(--ava-neutral-200, #e2e8f0)",
                                }}
                              >
                                {doc.badge === "camera" && <Camera className="w-[11px] h-[11px]" />}
                                {doc.badgeLabel}
                              </span>
                            )}
                          </p>
                          <p
                            style={{
                              margin: "3px 0 0",
                              fontSize: 12.5,
                              color: "var(--ava-fg-muted, var(--muted-foreground))",
                              lineHeight: 1.45,
                            }}
                          >
                            {doc.desc}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div
                    className="flex items-start gap-2.5"
                    style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--ava-border, var(--border))" }}
                  >
                    <PenLine
                      className="w-[15px] h-[15px] flex-shrink-0"
                      style={{ color: "var(--ava-fg-muted, var(--muted-foreground))", marginTop: 1 }}
                    />
                    <p
                      style={{
                        margin: 0,
                        fontSize: 12,
                        color: "var(--ava-fg-muted, var(--muted-foreground))",
                        lineHeight: 1.5,
                      }}
                    >
                      Además vas a completar declaraciones juradas (PEP y origen de fondos) dentro del
                      formulario — sin archivos.
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="flex items-start gap-2.5"
              style={{
                marginTop: 24,
                padding: "14px 16px",
                background: "var(--ava-muted, #f8fafc)",
                border: "1px solid var(--ava-border, var(--border))",
                borderRadius: 8,
              }}
            >
              <ShieldCheck
                className="w-4 h-4 flex-shrink-0"
                style={{ color: "var(--ava-primary, #0B3D91)", marginTop: 1 }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: "var(--ava-fg-muted, var(--muted-foreground))",
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: "var(--ava-fg, inherit)", fontWeight: 600 }}>
                  La lista exacta se confirma al iniciar el trámite.
                </strong>{" "}
                Según tu perfil y nivel de riesgo regulatorio, podemos pedirte documentación adicional
                durante la revisión.
              </p>
            </div>
          </div>

          {/* Compliance badges */}
          <div className="pt-6" style={{ marginTop: "clamp(40px, 6vw, 64px)", borderTop: "1px solid var(--ava-border, var(--border))" }}>
            <p
              className="text-center"
              style={{
                margin: 0,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--ava-fg-muted, var(--muted-foreground))",
              }}
            >
              Cumplimiento normativo argentino
            </p>
            <div
              className="flex justify-center items-center flex-wrap mt-4"
              style={{ gap: "clamp(20px, 4vw, 48px)" }}
            >
              {[
                { t: "UIF", s: "Res. 30/2017" },
                { t: "BCRA", s: "Apertura digital" },
                { t: "CNV", s: "Mercado de capitales" },
                { t: "Ley 25.326", s: "Datos personales" },
              ].map((b) => (
                <div key={b.t} className="text-center">
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: "0.02em" }}>{b.t}</p>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: 11,
                      color: "var(--ava-fg-muted, var(--muted-foreground))",
                    }}
                  >
                    {b.s}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Trust strip — verde */}
          <div
            className="flex items-center gap-3 flex-wrap mt-6"
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              background: "var(--ava-success-50, #f0fdf4)",
              border: "1px solid var(--ava-success-200, #bbf7d0)",
            }}
          >
            <Shield className="w-[18px] h-[18px] flex-shrink-0" style={{ color: "var(--ava-success-700, #15803d)" }} />
            <div className="flex-1 min-w-0">
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "var(--ava-success-700, #15803d)" }}>
                Conexión segura · TLS 1.3
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ava-success-700, #15803d)", opacity: 0.85 }}>
                Datos protegidos bajo Ley 25.326 · Marco regulatorio UIF · BCRA · CNV
              </p>
            </div>
          </div>
        </section>

        <footer
          className="text-center mt-auto"
          style={{
            borderTop: "1px solid var(--ava-border, var(--border))",
            padding: "20px 16px",
            background: "#fff",
          }}
        >
          <p style={{ margin: 0, fontSize: 11, color: "var(--ava-fg-muted, var(--muted-foreground))" }}>
            AVA Onboarding · © {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    );
  }

  // ============================================
  // AUTHENTICATED - Dashboard
  // ============================================
  return (
    <div className="min-h-screen bg-gray-50/50">
      <AppHeader />

      {/* Hero banner */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0B3D91 0%, #1565C0 40%, #1E88E5 70%, #3B8FF5 100%)" }}>
        <div className="absolute inset-0 opacity-15">
          <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ height: "60%" }}>
            <path fill="rgba(255,255,255,0.2)" d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,149.3C672,139,768,149,864,170.7C960,192,1056,224,1152,218.7C1248,213,1344,171,1392,149.3L1440,128L1440,320L0,320Z" />
          </svg>
        </div>
        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-white">
                Hola, {user.name?.split(" ")[0] || "Solicitante"}
              </h1>
              <p className="text-blue-100 mt-1">
                {activeRequest
                  ? "Continuá con tu solicitud de onboarding."
                  : isPJ
                    ? (companies && companies.length > 0
                      ? "Seleccioná una empresa para iniciar el trámite."
                      : "Registrá tu primera empresa para comenzar.")
                    : "Iniciá un nuevo trámite de alta."}
              </p>
            </div>
            {/* PH: botón Nueva solicitud (a menos que necesite verificar identidad) */}
            {!activeRequest && !isPJ && (
              <Button
                className="bg-white text-blue-700 hover:bg-blue-50 font-medium w-full sm:w-auto gap-2"
                style={{ height: "var(--ava-control-h-lg, 44px)" }}
                onClick={handleCreateRequest}
                disabled={createMutation.isPending || clientLoading || phNeedsVerification}
              >
                {createMutation.isPending || clientLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {createMutation.isPending ? "Creando..." : "Cargando..."}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Nueva solicitud
                  </>
                )}
              </Button>
            )}
            {/* PJ sin empresas → "Registrar empresa" que lleva al CompanyManager */}
            {isPJ && (!companies || companies.length === 0) && (
              <Button
                className="bg-white text-blue-700 hover:bg-blue-50 font-medium w-full sm:w-auto gap-2"
                style={{ height: "var(--ava-control-h-lg, 44px)" }}
                onClick={() => setOpenCompanyCreate(true)}
              >
                <Plus className="w-4 h-4" />
                Registrar empresa
              </Button>
            )}
            {/* PJ con empresas → "Nueva solicitud" */}
            {isPJ && companies && companies.length > 0 && (
              <Button
                className="bg-white text-blue-700 hover:bg-blue-50 font-medium w-full sm:w-auto gap-2"
                style={{ height: "var(--ava-control-h-lg, 44px)" }}
                onClick={handleCreateRequest}
                disabled={createMutation.isPending}
              >
                <Plus className="w-4 h-4" />
                Nueva solicitud
              </Button>
            )}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* PH: Identity verification required — tokens AVA warn */}
        {phNeedsVerification && !isPJ && user && (
          <div
            className="rounded-xl p-6 mb-6 border"
            style={{
              backgroundColor: "var(--ava-warn-50)",
              borderColor: "var(--ava-warn-200)",
            }}
          >
            <div className="flex items-start gap-3">
              <Shield
                className="h-6 w-6 shrink-0 mt-0.5"
                style={{ color: "var(--ava-warn-700)" }}
              />
              <div className="flex-1">
                <h3 className="font-semibold mb-1" style={{ color: "var(--ava-warn-700)" }}>
                  Verificación de identidad requerida
                </h3>
                <p className="text-sm mb-4" style={{ color: "var(--ava-warn-700)" }}>
                  Para iniciar un trámite de onboarding, primero debe verificar su identidad
                  con una foto de su documento y una selfie.
                </p>
                <Button
                  onClick={() => setLocation("/identity-verification")}
                  className="font-medium"
                  style={{ height: "var(--ava-control-h-lg, 44px)" }}
                >
                  Verificar mi identidad
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Verified — banner del kit v1: card con check circular + título + vigencia */}
        {verificationStatus === "aprobada" && (
          <div
            className="flex items-center gap-3.5 p-4 rounded-xl border flex-wrap"
            style={{
              backgroundColor: "var(--ava-success-50)",
              borderColor: "var(--ava-success-200)",
            }}
          >
            <div
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0"
              style={{ color: "var(--ava-success-700)" }}
            >
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm" style={{ color: "var(--ava-success-700)" }}>
                Tu identidad está verificada
              </div>
              <div className="text-xs mt-0.5" style={{ color: "var(--ava-success-700)", opacity: 0.8 }}>
                Verificada · vigencia 24 meses
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SECCIÓN EMPRESAS - Solo para PJ */}
        {/* ============================================ */}
        {isPJ && (
          <CompanyManager
            onSelectCompany={undefined}
            openCreate={openCompanyCreate}
            onOpenCreateChange={setOpenCompanyCreate}
          />
        )}

        {/* Loading */}
        {requestsLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse border rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                  <div className="w-20 h-6 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Active request - prominent card */}
        {!requestsLoading && activeRequest && (() => {
          const redirect = getRedirectPath(activeRequest);
          const statusConfig = getStatusConfig(activeRequest.status);

          // Determine step progress — PJ includes Beneficiarios/Verificación, PH does not.
          // Sprint Lifecycle v2: stepper agrega "En proceso" (PROCESSING) y
          // "Finalizado" (terminales) al final. Cada etapa se marca `done`
          // cuando el request alcanzó (o superó) ese hito.
          const isPJ = activeRequest.customerType === "PERSONA_JURIDICA";
          const isFinal = FINAL_STATUSES.includes(activeRequest.status);
          const isSubmittedOrLater =
            activeRequest.status === "SUBMITTED" ||
            activeRequest.status === "PROCESSING" ||
            isFinal;
          const isProcessingOrLater =
            activeRequest.status === "PROCESSING" || isFinal;
          const steps = isPJ
            ? [
                { label: "Documentos", done: activeRequest.status !== "DRAFT" },
                { label: "Revisión", done: activeRequest.allInitialDocsComplete || false },
                { label: "Beneficiarios", done: activeRequest.hasAnalysis || false },
                { label: "Verificación", done: isSubmittedOrLater },
                { label: "Enviado", done: isSubmittedOrLater },
                { label: "En proceso", done: isProcessingOrLater },
                { label: "Finalizado", done: isFinal },
              ]
            : [
                { label: "Documentos", done: activeRequest.status !== "DRAFT" },
                { label: "Revisión", done: activeRequest.allInitialDocsComplete || false },
                { label: "Enviado", done: isSubmittedOrLater },
                { label: "En proceso", done: isProcessingOrLater },
                { label: "Finalizado", done: isFinal },
              ];
          const completedSteps = steps.filter((s) => s.done).length;
          const progressPercent = Math.round((completedSteps / steps.length) * 100);

          // Sprint Lifecycle v2: banner específico cuando el ejecutivo
          // rechazó documentos y el cliente debe reemplazarlos.
          const showRejectedDocsBanner =
            activeRequest.status === "PROCESSING" &&
            !!(activeRequest as any).hasDocsNeedingAction;
          const rejectedDocsCount =
            (activeRequest as any).actionableDocCount ?? 0;

          return (
            <div className="space-y-2">
              <h3 className="ava-overline">Solicitud activa</h3>

              {/* Sprint Lifecycle v2: banner amber para PROCESSING con docs
                  rechazados. El cliente puede subir reemplazos en `/documents`
                  mientras el ejecutivo sigue revisando los demás docs. */}
              {showRejectedDocsBanner && (
                <div
                  role="status"
                  aria-live="polite"
                  className="rounded-lg border p-4 flex items-start gap-3"
                  style={{
                    backgroundColor: "var(--ava-warn-50)",
                    borderColor: "var(--ava-warn-200)",
                  }}
                >
                  <AlertTriangle
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    style={{ color: "var(--ava-warn-700)" }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-semibold"
                      style={{ color: "var(--ava-warn-700)" }}
                    >
                      {rejectedDocsCount === 1
                        ? "El ejecutivo rechazó 1 documento."
                        : `El ejecutivo rechazó ${rejectedDocsCount} documentos.`}
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: "var(--ava-warn-700)" }}
                    >
                      Revisá los detalles y subí los reemplazos.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() =>
                      setLocation(`/onboarding/${activeRequest.requestId}/documents`)
                    }
                    className="font-medium"
                  >
                    Ir a documentos
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}

              <Card className="border shadow-sm">
                <CardContent className="py-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <MonoText className="text-sm text-primary">{activeRequest.requestId}</MonoText>
                        <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                        <StatusBadge tone="info">
                          {activeRequest.customerType === "PERSONA_JURIDICA" ? "PJ" : "PH"}
                        </StatusBadge>
                        {(activeRequest as any).hasDocsNeedingAction && (
                          <StatusBadge tone="danger">
                            {(activeRequest as any).actionableDocCount === 1
                              ? "1 documento rechazado"
                              : `${(activeRequest as any).actionableDocCount} documentos rechazados`}
                          </StatusBadge>
                        )}
                        {(activeRequest as any).hasUnconfirmedDocs &&
                          (activeRequest.status === "DRAFT" || activeRequest.status === "PROCESSING") && (
                          <StatusBadge tone="warn">
                            {(activeRequest as any).unconfirmedDocCount === 1
                              ? "1 documento por confirmar"
                              : `${(activeRequest as any).unconfirmedDocCount} documentos por confirmar`}
                          </StatusBadge>
                        )}
                        {(activeRequest as any).hasPendingAdditionalDocs && (
                          <StatusBadge tone="warn">
                            Documentos adicionales requeridos
                          </StatusBadge>
                        )}
                        {!(activeRequest as any).allInitialDocsComplete &&
                          !(activeRequest as any).hasPendingAdditionalDocs &&
                          !(activeRequest as any).hasDocsNeedingAction && (
                          <StatusBadge tone="warn">
                            Documento requerido pendiente
                          </StatusBadge>
                        )}
                      </div>
                      <h3 className="ava-h3">
                        {activeRequest.clientName || (activeRequest as any).entityName || (activeRequest.customerType === "PERSONA_JURIDICA" ? "Persona Jurídica" : "Persona Humana")}
                      </h3>
                      {activeRequest.clientIdentifier && (
                        <p className="text-xs text-muted-foreground">CUIT: <MonoText>{activeRequest.clientIdentifier}</MonoText></p>
                      )}
                      <p className="text-sm text-muted-foreground mt-1">
                        Próximo paso:{" "}
                        <span className="font-medium text-foreground">
                          {(activeRequest as any).hasDocsNeedingAction
                            ? "Reemplazar documentos rechazados"
                            : !(activeRequest as any).allInitialDocsComplete
                              ? "Documento requerido pendiente de carga"
                              : redirect.stepName}
                        </span>
                      </p>
                    </div>
                    <Button onClick={() => handleViewRequest(activeRequest)} className="font-semibold w-full sm:w-auto">
                      Continuar
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  {/* Step progress */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Progreso</span>
                      <span className="text-xs font-medium">{progressPercent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                    </div>
                    <div className="flex justify-between mt-2">
                      {steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${step.done ? "bg-blue-500" : "bg-gray-300"}`} />
                          <span className={`text-[10px] sm:text-xs ${step.done ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Other active requests (if more than one) */}
        {!requestsLoading && activeRequests.length > 1 && (
          <div className="space-y-2">
            <h3 className="ava-overline">Otras solicitudes activas</h3>
            {activeRequests.slice(1).map((req: any) => {
              const redirect = getRedirectPath(req);
              const statusConfig = getStatusConfig(req.status);
              return (
                <Card key={req.id} className="hover:shadow-sm transition-shadow cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" tabIndex={0} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') handleViewRequest(req); }} onClick={() => handleViewRequest(req)}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-1 h-10 rounded-full ${statusConfig.barColor}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <MonoText className="text-sm text-primary">{req.requestId}</MonoText>
                            <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {req.clientName || redirect.stepName}
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">Continuar</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Completed requests */}
        {!requestsLoading && completedRequests.length > 0 && (
          <div className="space-y-2">
            <h3 className="ava-overline">Solicitudes finalizadas</h3>
            {completedRequests.map((req: any) => {
              const statusConfig = getStatusConfig(req.status);
              return (
                <Card key={req.id} className="hover:shadow-sm transition-shadow cursor-pointer opacity-80 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" tabIndex={0} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') handleViewRequest(req); }} onClick={() => handleViewRequest(req)}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-1 h-10 rounded-full ${statusConfig.barColor}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <MonoText className="text-sm">{req.requestId}</MonoText>
                            <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {req.clientName || (req.customerType === "PERSONA_JURIDICA" ? "Persona Jurídica" : "Persona Humana")}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Nueva solicitud - botón visible en mobile para PH */}
        {!requestsLoading && !activeRequest && !isPJ && (
          <div className="md:hidden">
            <Button
              className="w-full"
              size="lg"
              onClick={handleCreateRequest}
              disabled={createMutation.isPending || clientLoading || phNeedsVerification}
            >
              {createMutation.isPending || clientLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{createMutation.isPending ? 'Creando solicitud...' : 'Cargando...'}</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Nueva solicitud<ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        )}

        {/* Nueva solicitud - botón mobile para PJ */}
        {!requestsLoading && isPJ && companies && companies.length > 0 && (
          <div className="md:hidden">
            <Button
              className="w-full"
              size="lg"
              onClick={handleCreateRequest}
              disabled={createMutation.isPending}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva solicitud
            </Button>
          </div>
        )}

        {/* Sin solicitudes — EmptyState DS (mock kit v2 HomePJScreen "Mis trámites") */}
        {!requestsLoading && !activeRequest && activeRequests.length === 0 && completedRequests.length === 0 && !isPJ && (
          <section>
            <div className="mb-3">
              <h3 className="ava-overline">Mis trámites</h3>
              <p className="text-xs text-muted-foreground mt-1">Solicitudes de onboarding iniciadas.</p>
            </div>
            <EmptyState
              icon={<FileSearch className="w-6 h-6" />}
              title="No tenés solicitudes de onboarding aún."
              body={
                <>
                  Usá el botón <strong className="font-semibold text-foreground">Nueva solicitud</strong> para comenzar tu trámite de alta.
                </>
              }
              actionLabel="Nueva solicitud"
              onAction={handleCreateRequest}
            />
          </section>
        )}

        {/* Sin solicitudes PJ con empresas — EmptyState DS */}
        {!requestsLoading && !activeRequest && activeRequests.length === 0 && completedRequests.length === 0 && isPJ && companies && companies.length > 0 && (
          <section>
            <div className="mb-3">
              <h3 className="ava-overline">Mis trámites</h3>
              <p className="text-xs text-muted-foreground mt-1">Solicitudes de onboarding iniciadas.</p>
            </div>
            <EmptyState
              icon={<FileSearch className="w-6 h-6" />}
              title="No tenés solicitudes de onboarding aún."
              body={
                <>
                  Usá el botón <strong className="font-semibold text-foreground">Nueva solicitud</strong> y seleccioná una empresa para comenzar.
                </>
              }
              actionLabel="Nueva solicitud"
              onAction={handleCreateRequest}
            />
          </section>
        )}
      </main>

      {/* Dialog para seleccionar empresa al crear solicitud (PJ) */}
      <Dialog open={showNewRequestDialog} onOpenChange={setShowNewRequestDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Nueva solicitud
            </DialogTitle>
            <DialogDescription>
              Seleccioná la empresa para la cual querés iniciar el trámite de alta.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <CompanyManager
              onSelectCompany={(company) => setSelectedCompanyId(company.id)}
              selectedCompanyId={selectedCompanyId}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowNewRequestDialog(false); setSelectedCompanyId(null); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateRequestWithCompany}
              disabled={!selectedCompanyId || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</>
              ) : (
                <><ArrowRight className="w-4 h-4 mr-2" />Iniciar Solicitud</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <footer className="border-t py-6 mt-8">
        <div className="container mx-auto px-6 text-center text-xs text-muted-foreground">
          <p>AVA Onboarding · © {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
